import { useState, useCallback, useMemo } from 'react';
import { getVendorMetadata } from '../../../../../lib/vendorCoverage';
import { SAMPLE_PRODUCTS } from '../PillarTemplate';

/**
 * Build a map of vendor name -> array of { category, product, pillar } objects
 * This tells us what categories each vendor can cover with which products.
 */
function buildVendorCoverageMap() {
  const coverageMap = {};

  Object.entries(SAMPLE_PRODUCTS).forEach(([pillar, products]) => {
    products.forEach(({ name: vendorName, product, category }) => {
      if (!vendorName || vendorName === 'Various') return;

      if (!coverageMap[vendorName]) {
        coverageMap[vendorName] = [];
      }

      coverageMap[vendorName].push({
        category,
        product,
        pillar
      });
    });
  });

  return coverageMap;
}

// Pre-compute the coverage map (it's static data)
const VENDOR_COVERAGE_MAP = buildVendorCoverageMap();

/**
 * View modes for vendor highlighting
 * - 'potential': Shows all nodes where vendor has coverage (what they COULD cover)
 * - 'current': Shows only nodes where vendor is already deployed
 */
export const VIEW_MODES = {
  POTENTIAL: 'potential',
  CURRENT: 'current'
};

/**
 * Hook for managing vendor highlight overlay in the Architecture Diagram.
 *
 * When a vendor is selected, it highlights ALL nodes where that vendor
 * has a product available - showing the vendor's full coverage potential,
 * not just what's currently deployed.
 */
export function useArchitectureVendorOverlay() {
  const [selectedVendorNames, setSelectedVendorNames] = useState([]);
  const [viewMode, setViewMode] = useState(VIEW_MODES.POTENTIAL);

  /**
   * Toggle a vendor's selection state
   */
  const toggleVendor = useCallback((vendorName) => {
    setSelectedVendorNames(prev => {
      if (prev.includes(vendorName)) {
        return prev.filter(n => n !== vendorName);
      }
      return [...prev, vendorName];
    });
  }, []);

  /**
   * Clear all selected vendors
   */
  const clearVendors = useCallback(() => {
    setSelectedVendorNames([]);
  }, []);

  /**
   * Get the combined coverage of all selected vendors
   * Returns a map of category -> array of { vendorName, product, color, logo }
   */
  const selectedVendorsCoverage = useMemo(() => {
    const coverage = {};

    selectedVendorNames.forEach(vendorName => {
      const vendorProducts = VENDOR_COVERAGE_MAP[vendorName] || [];
      const meta = getVendorMetadata(vendorName);

      vendorProducts.forEach(({ category, product }) => {
        if (!coverage[category]) {
          coverage[category] = [];
        }
        coverage[category].push({
          vendorName,
          product,
          displayColor: meta.displayColor,
          logoUrl: meta.logoUrl
        });
      });
    });

    return coverage;
  }, [selectedVendorNames]);

  /**
   * Check if a node should be highlighted based on view mode:
   * - 'potential': Highlight if any selected vendor has a product covering this category
   * - 'current': Highlight only if the node's vendor matches a selected vendor
   */
  const isNodeHighlighted = useCallback((node) => {
    if (!node?.category || selectedVendorNames.length === 0) return false;
    // Input/output nodes are never highlighted
    if (node.isInput || node.isOutput) return false;

    if (viewMode === VIEW_MODES.CURRENT) {
      // Current state: only highlight if node's vendor is one of the selected vendors
      return node.vendorName && selectedVendorNames.includes(node.vendorName);
    }

    // Potential state: highlight if any selected vendor covers this category
    return !!selectedVendorsCoverage[node.category];
  }, [selectedVendorNames, selectedVendorsCoverage, viewMode]);

  /**
   * Get vendors covering this node based on view mode:
   * - 'potential': Returns ALL selected vendors that cover this category
   * - 'current': Returns only the node's deployed vendor if it's selected
   * Returns array of { vendorName, product, displayColor, logoUrl }
   */
  const getNodeCoverage = useCallback((node) => {
    if (!node?.category) return [];

    if (viewMode === VIEW_MODES.CURRENT) {
      // Current state: only return coverage if node's vendor is selected
      if (!node.vendorName || !selectedVendorNames.includes(node.vendorName)) {
        return [];
      }
      const meta = getVendorMetadata(node.vendorName);
      return [{
        vendorName: node.vendorName,
        product: node.productName || node.label,
        displayColor: meta.displayColor,
        logoUrl: meta.logoUrl
      }];
    }

    // Potential state: return all selected vendors that cover this category
    return selectedVendorsCoverage[node.category] || [];
  }, [selectedVendorsCoverage, viewMode, selectedVendorNames]);

  /**
   * Get the highlight color for a node.
   * If multiple vendors cover this category, use the first one's color.
   */
  const getHighlightColor = useCallback((node) => {
    const coverage = getNodeCoverage(node);
    if (coverage.length === 0) return null;
    return coverage[0].displayColor;
  }, [getNodeCoverage]);

  /**
   * Get vendor logo URL for a highlighted node.
   * Uses the first vendor's logo if multiple cover this category.
   */
  const getVendorLogo = useCallback((node) => {
    const coverage = getNodeCoverage(node);
    if (coverage.length === 0) return null;
    return coverage[0].logoUrl;
  }, [getNodeCoverage]);

  /**
   * Get the vendor's product name that covers this node's category.
   * Returns the product from the first selected vendor that covers it.
   */
  const getVendorProduct = useCallback((node) => {
    const coverage = getNodeCoverage(node);
    if (coverage.length === 0) return null;
    return {
      vendorName: coverage[0].vendorName,
      productName: coverage[0].product
    };
  }, [getNodeCoverage]);

  /**
   * Whether any vendors are currently selected
   */
  const hasOverlay = selectedVendorNames.length > 0;

  /**
   * Get selected vendors with their metadata for display
   */
  const selectedVendorsWithMeta = useMemo(() => {
    return selectedVendorNames.map(name => ({
      name,
      ...getVendorMetadata(name),
      categoryCount: (VENDOR_COVERAGE_MAP[name] || []).length
    }));
  }, [selectedVendorNames]);

  /**
   * Toggle between current and potential view modes
   */
  const toggleViewMode = useCallback(() => {
    setViewMode(prev =>
      prev === VIEW_MODES.POTENTIAL ? VIEW_MODES.CURRENT : VIEW_MODES.POTENTIAL
    );
  }, []);

  return {
    selectedVendorNames,
    selectedVendorsWithMeta,
    toggleVendor,
    clearVendors,
    isNodeHighlighted,
    getNodeCoverage, // Returns ALL vendors covering a category for split borders
    getHighlightColor,
    getVendorLogo,
    getVendorProduct,
    hasOverlay,
    // View mode controls
    viewMode,
    setViewMode,
    toggleViewMode,
    VIEW_MODES,
    // Export for debugging/display
    vendorCoverageMap: VENDOR_COVERAGE_MAP
  };
}

export default useArchitectureVendorOverlay;
